import React, { useEffect, useState } from 'react';

import { WarehouseInventoryV2Api } from 'api/warehouseInventoryV2';

import { FilterMenuControlTW } from './filterComponentTailwind/FilterMenuControlTW';
import { SelectTW } from './filterComponentTailwind/SelectTW';

import './inventoryHistoryV2.css';

const warehouseApi = new WarehouseInventoryV2Api();

function FilterAndGroupByContainer({
    facilityId,
    initialFilters = null,
    initialGroupBy = 'sku',
    initialSummarizePackout = true,
    onFiltersChange,
    onGroupChange,
    onSummarizePackoutChange,
    setNotification,
}) {
    const [groupBy, setGroupBy] = useState(initialGroupBy);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [reasonCodeOptions, setReasonCodeOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [summarizePackout, setSummarizePackout] = useState(initialSummarizePackout);

    const fetchSources = async () => {
        if (!facilityId) {
            setNotification({ text: 'No facility ID provided', level: 'error' });
            return;
        }

        const response = await warehouseApi.getInventoryOptions(facilityId);

        if (!response.status) {
            setSourceOptions([]);
            setNotification({ text: response.message ?? 'Error fetching sources', level: 'error' });
        }
        setSourceOptions(response?.data?.companySources ?? []);
    };

    const fetchReasonCodes = async () => {
        const response = await warehouseApi.getAdjustReasons();

        if (!response.status) {
            setReasonCodeOptions([]);
            setNotification({ text: response.message ?? 'Error fetching reason codes', level: 'error' });
            return;
        }

        setReasonCodeOptions(response?.data?.reasons ?? []);
    };

    useEffect(() => {
        const fetchOptions = async () => {
            if (!facilityId) {
                setNotification({ text: 'No facility ID provided', level: 'error' });
                return;
            }

            setLoadingOptions(true);
            try {
                await Promise.all([fetchSources(), fetchReasonCodes()]);
            } catch (error) {
                setNotification({ text: 'Error fetching options', level: 'error' });
            } finally {
                setLoadingOptions(false);
            }
        };

        fetchOptions();
    }, [facilityId]);

    useEffect(() => {
        setGroupBy(initialGroupBy);
    }, [initialGroupBy]);

    useEffect(() => {
        setSummarizePackout(initialSummarizePackout);
    }, [initialSummarizePackout]);

    const handleGroupChange = (value) => {
        setGroupBy(value);
        if (onGroupChange) {
            onGroupChange(value);
        }
    };

    const handleSummarizePackoutChange = (e) => {
        const newValue = e.target.checked;
        setSummarizePackout(newValue);
        if (onSummarizePackoutChange) {
            onSummarizePackoutChange(newValue);
        }
    };

    const INVENTORY_HISTORY_FILTERS = [
        {
            label: 'Date Range',
            type: 'dateRange',
            filterKey: 'dateRange',
            props: {
                includeTime: true,
            },
            deletable: false,
        },
        {
            label: 'Event Type',
            type: 'multiCheck',
            filterKey: 'eventType',
            props: {
                options: [
                    'Moved Event',
                    'Inventory Adjusted Event',
                    'Packout Event',
                    'Received Event',
                    'Expiration Date Changed',
                    'QC Hold Status Change',
                    'Cycle Count Event',
                    'Prepped Event',
                ],
            },
            deletable: true,
        },
        {
            label: 'SKU',
            type: 'textInput',
            filterKey: 'sku',
            props: {
                placeholder: 'Enter SKU...',
                formatValue: (value) => value.toUpperCase(),
                errorMessage: 'Please enter a valid SKU',
                multiple: true,
            },
            deletable: true,
        },
        {
            label: 'Item Name',
            type: 'textInput',
            filterKey: 'itemName',
            props: {
                placeholder: 'Enter Item Name...',
                formatValue: (value) => value.toUpperCase(),
                errorMessage: 'Please enter a valid name',
                multiple: true,
            },
            deletable: true,
        },
        {
            label: 'Partner SKU',
            type: 'textInput',
            filterKey: 'partnerSku',
            props: {
                placeholder: 'Enter Partner SKU...',
                formatValue: (value) => value.toUpperCase(),
                errorMessage: 'Please enter a valid partner sku',
                multiple: true,
            },
            deletable: true,
        },
        {
            label: 'Lot',
            type: 'textInput',
            filterKey: 'lot',
            props: {
                placeholder: 'Enter Lot...',
                formatValue: (value) => value.toUpperCase(),
                errorMessage: 'Please enter a valid Lot',
                multiple: true,
            },
            deletable: true,
        },
        {
            label: 'Location',
            type: 'textInput',
            filterKey: 'location',
            props: {
                placeholder: 'Enter Location...',
                formatValue: (value) => value.toUpperCase(),
                errorMessage: 'Please enter a valid Location',
                multiple: true,
            },
            deletable: true,
        },
        {
            label: 'Source',
            type: 'select',
            filterKey: 'source',
            props: {
                options: sourceOptions,
                placeholder: 'Search sources...',
                loading: loadingOptions,
                multiple: true,
                searchable: false,
            },
            deletable: true,
        },
        {
            label: 'Reason Code',
            type: 'select',
            filterKey: 'reason',
            props: {
                options: reasonCodeOptions,
                placeholder: 'Select reason code...',
                loading: loadingOptions,
                multiple: true,
                searchable: true,
            },
            deletable: true,
        },
        {
            label: 'User',
            type: 'textInput',
            filterKey: 'user',
            props: {
                placeholder: 'Enter User...',
                formatValue: (value) => value.toUpperCase(),
                errorMessage: 'Please enter a valid User',
                multiple: true,
            },
            deletable: true,
        },
        {
            label: 'Pallet ID',
            type: 'textInput',
            filterKey: 'palletId',
            props: {
                placeholder: 'Enter Pallet ID...',
                formatValue: (value) => value.toUpperCase(),
                errorMessage: 'Please enter a valid Pallet ID',
                multiple: true,
            },
            deletable: true,
        },
    ];

    const groupByOptions = [
        { value: 'sku', label: 'Group By Sku' },
        { value: 'lot', label: 'Group By Lot' },
        // Commented options kept for future reference
        // { value: 'none', label: 'No Grouping' },
        // { value: 'location', label: 'Group By Location' },
    ];

    return (
        <div className="inventory-history-v2">
            <div className="flex flex-col gap-4">
                {loadingOptions ? (
                    <div className="p-4 bg-white rounded standard-shadow flex justify-center items-center">
                        <div className="w-8 h-8 spinner" />
                    </div>
                ) : (
                    <FilterMenuControlTW
                        filterConfigs={INVENTORY_HISTORY_FILTERS}
                        onFilterChange={onFiltersChange}
                        initialFilters={initialFilters}
                    />
                )}

                <div className="flex items-end p-4 bg-white">
                    <div className="relative min-w-[200px]">
                        <SelectTW
                            label="Group By"
                            options={groupByOptions}
                            value={groupBy}
                            onChange={handleGroupChange}
                            placeholder="Select grouping..."
                        />
                    </div>
                    <div className="summarize-packout-container">
                        <div className="summarize-packout-checkbox">
                            <input
                                type="checkbox"
                                id="summarizePackout"
                                checked={summarizePackout}
                                onChange={handleSummarizePackoutChange}
                                className="summarize-packout-input"
                            />
                        </div>
                        <label htmlFor="summarizePackout" className="summarize-packout-label">
                            Summarize Packout Events
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { FilterAndGroupByContainer };
